import { useState } from 'react';
import { Link } from 'react-router-dom';
import type { TaskListItem } from '../types/task';
import { TaskStatus, AgentType, AGENT_TYPE_INFO } from '../types/task';

interface KanbanBoardProps {
  tasks: TaskListItem[];
  isAdmin: boolean;
  showAllUsers: boolean;
  onTaskReorder?: (taskId: number, newStatus: TaskStatus, newPosition: number) => Promise<void>;
  onTaskArchive?: (taskId: number) => Promise<void>;
}

const getStatusColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.DRAFT:
      return 'bg-yellow-50 border-yellow-200';
    case TaskStatus.PENDING:
      return 'bg-gray-50 border-gray-200';
    case TaskStatus.PROCESSING:
      return 'bg-blue-50 border-blue-200';
    case TaskStatus.COMPLETED:
      return 'bg-green-50 border-green-200';
    case TaskStatus.FAILED:
      return 'bg-red-50 border-red-200';
    default:
      return 'bg-gray-50 border-gray-200';
  }
};

const getStatusLabel = (status: TaskStatus): string => {
  return status.charAt(0).toUpperCase() + status.slice(1);
};

const getStatusBadgeColor = (status: TaskStatus): string => {
  switch (status) {
    case TaskStatus.DRAFT:
      return 'bg-yellow-100 text-yellow-700';
    case TaskStatus.PENDING:
      return 'bg-gray-100 text-gray-700';
    case TaskStatus.PROCESSING:
      return 'bg-blue-100 text-blue-700';
    case TaskStatus.COMPLETED:
      return 'bg-green-100 text-green-700';
    case TaskStatus.FAILED:
      return 'bg-red-100 text-red-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

export default function KanbanBoard({ tasks, isAdmin, showAllUsers, onTaskReorder, onTaskArchive }: KanbanBoardProps) {
  const [draggedTask, setDraggedTask] = useState<TaskListItem | null>(null);
  const [dragOverColumn, setDragOverColumn] = useState<TaskStatus | 'archive' | null>(null);
  const [dragOverTask, setDragOverTask] = useState<number | null>(null);
  const [archivedTasks, setArchivedTasks] = useState<TaskListItem[]>([]);

  // Group tasks by status
  const tasksByStatus = {
    [TaskStatus.DRAFT]: tasks.filter(t => t.status === TaskStatus.DRAFT),
    [TaskStatus.PENDING]: tasks.filter(t => t.status === TaskStatus.PENDING),
    [TaskStatus.PROCESSING]: tasks.filter(t => t.status === TaskStatus.PROCESSING),
    [TaskStatus.COMPLETED]: tasks.filter(t => t.status === TaskStatus.COMPLETED),
    [TaskStatus.FAILED]: tasks.filter(t => t.status === TaskStatus.FAILED),
  };

  // Only draft and pending columns support drag-and-drop
  const isDraggableColumn = (status: TaskStatus) => {
    return status === TaskStatus.DRAFT || status === TaskStatus.PENDING;
  };

  // Check if a task can be archived (COMPLETED, DRAFT, and FAILED tasks)
  const canArchiveTask = (task: TaskListItem) => {
    return task.status === TaskStatus.COMPLETED || task.status === TaskStatus.DRAFT || task.status === TaskStatus.FAILED;
  };

  const handleDragStart = (e: React.DragEvent, task: TaskListItem) => {
    // Allow dragging from draft/pending columns, or from completed/draft for archiving
    if (!isDraggableColumn(task.status) && !canArchiveTask(task)) return;
    setDraggedTask(task);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, target: TaskStatus | 'archive') => {
    if (target === 'archive') {
      // Only allow archive drop if task can be archived
      if (draggedTask && canArchiveTask(draggedTask)) {
        e.preventDefault();
        setDragOverColumn('archive');
        e.dataTransfer.dropEffect = 'move';
      }
    } else if (!isDraggableColumn(target)) {
      return;
    } else {
      e.preventDefault();
      setDragOverColumn(target);
      e.dataTransfer.dropEffect = 'move';
    }
  };

  const handleDragLeave = () => {
    setDragOverColumn(null);
    setDragOverTask(null);
  };

  const handleTaskDragOver = (e: React.DragEvent, taskId: number, status: TaskStatus) => {
    if (!draggedTask || !isDraggableColumn(status)) return;
    e.preventDefault();
    e.stopPropagation();
    setDragOverTask(taskId);
    setDragOverColumn(status);
  };

  const handleDrop = async (e: React.DragEvent, target: TaskStatus | 'archive') => {
    e.preventDefault();
    setDragOverColumn(null);
    setDragOverTask(null);

    if (!draggedTask) return;

    // Handle archive drop
    if (target === 'archive' && canArchiveTask(draggedTask) && onTaskArchive) {
      // Add to archived tasks list for visual feedback
      setArchivedTasks(prev => [...prev, draggedTask]);

      // Call the archive API
      await onTaskArchive(draggedTask.id);
      setDraggedTask(null);
      return;
    }

    // Handle status column drop (reordering)
    if (target !== 'archive' && isDraggableColumn(target)) {
      const targetTasks = tasksByStatus[target];

      // Calculate new position
      let newPosition = targetTasks.length; // Default to end

      if (dragOverTask !== null) {
        // Find the index of the task we're hovering over
        const hoverIndex = targetTasks.findIndex(t => t.id === dragOverTask);
        if (hoverIndex !== -1) {
          newPosition = hoverIndex;
        }
      }

      // Call the reorder API - it will handle both status changes and reordering
      if (onTaskReorder) {
        await onTaskReorder(draggedTask.id, target, newPosition);
      }
    }

    setDraggedTask(null);
  };

  const handleDragEnd = () => {
    setDraggedTask(null);
    setDragOverColumn(null);
  };

  const columns = [
    TaskStatus.DRAFT,
    TaskStatus.PENDING,
    TaskStatus.PROCESSING,
    TaskStatus.COMPLETED,
    TaskStatus.FAILED,
  ];

  return (
    <div className="flex gap-4 overflow-x-auto items-stretch">
      {columns.map((status, index) => {
        const columnTasks = tasksByStatus[status];
        const isDraggable = isDraggableColumn(status);
        const isDragOver = dragOverColumn === status;
        const isFirstColumn = index === 0;

        return (
          <div
            key={status}
            className={`flex-shrink-0 w-80 flex flex-col ${isFirstColumn ? 'ml-4' : ''}`}
            onDragOver={isDraggable ? (e) => handleDragOver(e, status) : undefined}
            onDragLeave={isDraggable ? handleDragLeave : undefined}
            onDrop={isDraggable ? (e) => handleDrop(e, status) : undefined}
          >
            {/* Column Header */}
            <div className={`p-4 rounded-t-lg border-t border-x ${getStatusColor(status)}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{getStatusLabel(status)}</h3>
                <span className="text-sm text-gray-600 bg-white px-2 py-1 rounded-full">
                  {columnTasks.length}
                </span>
              </div>
              {isDraggable && (
                <p className="text-xs text-gray-500 mt-1">Drag to reorder</p>
              )}
            </div>

            {/* Column Content */}
            <div
              className={`min-h-96 flex-1 p-3 rounded-b-lg border-b border-x transition-colors ${
                getStatusColor(status)
              } ${isDragOver ? 'ring-2 ring-black ring-opacity-50' : ''}`}
            >
              <div className="space-y-3">
                {columnTasks.length === 0 ? (
                  <p className="text-sm text-gray-400 text-center py-8">No tasks</p>
                ) : (
                  columnTasks.map((task) => {
                    const canDrag = isDraggable || canArchiveTask(task);
                    const isBeingDraggedOver = dragOverTask === task.id;
                    return (
                      <Link
                        key={task.id}
                        to={`/tasks/${task.id}`}
                        target="_blank"
                        rel="opener"
                        draggable={canDrag}
                        onDragStart={canDrag ? (e) => handleDragStart(e, task) : undefined}
                        onDragOver={isDraggable ? (e) => handleTaskDragOver(e, task.id, status) : undefined}
                        onDragEnd={canDrag ? handleDragEnd : undefined}
                        className={`block bg-white border border-gray-200 rounded-lg p-4 shadow-sm hover:shadow-md transition-all relative ${
                          canDrag ? 'cursor-move hover:border-black' : 'cursor-pointer'
                        } ${draggedTask?.id === task.id ? 'opacity-50' : ''} ${
                          isBeingDraggedOver ? 'border-t-4 border-t-blue-500' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className={`text-sm font-medium text-gray-900 ${
                            task.unread ? 'font-semibold' : ''
                          }`}>
                            {task.title}
                          </h4>
                          {task.unread && (
                            <span className="w-2 h-2 bg-blue-500 rounded-full inline-block flex-shrink-0"></span>
                          )}
                        </div>

                        {isAdmin && showAllUsers && task.created_by_username && (
                          <p className="text-xs text-gray-500 mb-2">
                            By: {task.created_by_fullname || task.created_by_username}
                          </p>
                        )}

                        <p className="text-xs text-gray-600 line-clamp-2 mb-3">
                          {task.description}
                        </p>

                        <div className="flex items-center justify-between text-xs text-gray-400">
                          <span>{new Date(task.created_at).toLocaleDateString()}</span>
                          <div className="flex items-center gap-1">
                            {/* Agent Type Badge */}
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              task.agent_type === AgentType.RIDER
                                ? 'bg-blue-100 text-blue-700'
                                : task.agent_type === AgentType.HUSARZ
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-gray-100 text-gray-600'
                            }`}>
                              {AGENT_TYPE_INFO[task.agent_type || AgentType.AGENT_128].name}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs ${getStatusBadgeColor(task.status)}`}>
                              #{task.id}
                            </span>
                          </div>
                        </div>
                      </Link>
                    );
                  })
                )}
              </div>
            </div>
          </div>
        );
      })}

      {/* Archive Column */}
      <div
        className="flex-shrink-0 w-80 mr-4 flex flex-col"
        onDragOver={(e) => handleDragOver(e, 'archive')}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, 'archive')}
      >
        {/* Column Header */}
        <div className="p-4 rounded-t-lg border-t border-x bg-gray-100 border-gray-300">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">Archive</h3>
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </div>
          <p className="text-xs text-gray-500 mt-1">Drop completed/failed/draft tasks</p>
        </div>

        {/* Column Content */}
        <div
          className={`min-h-96 flex-1 p-3 rounded-b-lg border-b border-x transition-colors bg-gray-50 border-gray-300 ${
            dragOverColumn === 'archive' ? 'ring-2 ring-black ring-opacity-50 bg-gray-100' : ''
          }`}
        >
          {archivedTasks.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center py-8">
                <svg className="w-16 h-16 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                </svg>
                <p className="text-sm text-gray-400">
                  Drag completed, failed,<br />or draft tasks here to archive
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {archivedTasks.map((task) => (
                <div
                  key={task.id}
                  className="block bg-white border border-gray-300 rounded-lg p-4 shadow-sm opacity-75"
                >
                  <div className="flex items-center mb-2">
                    <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <h4 className="text-sm font-medium text-gray-600">
                      {task.title}
                    </h4>
                  </div>

                  {isAdmin && showAllUsers && task.created_by_username && (
                    <p className="text-xs text-gray-400 mb-2">
                      By: {task.created_by_fullname || task.created_by_username}
                    </p>
                  )}

                  <p className="text-xs text-gray-500 line-clamp-2 mb-3">
                    {task.description}
                  </p>

                  <div className="flex items-center justify-between text-xs text-gray-400">
                    <span>{new Date(task.created_at).toLocaleDateString()}</span>
                    <span className="px-2 py-0.5 rounded-full text-xs bg-gray-200 text-gray-600">
                      Archived #{task.id}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
